import React, { useEffect, useState } from 'react';

import CloseIcon from '@mui/icons-material/Close';
import { Box, Button, FormControlLabel, IconButton, Stack, Switch, TextField, Typography } from '@mui/material';

import type { BoardOperation } from './editor';

type RawSettingsProps = { title: string; values: Record<string, unknown>; skip?: Array<string>; disabled: boolean; onSet: (key: string, value: unknown) => void; inline?: boolean };

const grouped = (value: unknown) => value !== null && typeof value === 'object' && !Array.isArray(value);

export function replaceSetting(id: string, key: string, value: unknown): Array<BoardOperation> {
  return grouped(value) ? [{ op: 'set', id, patch: { [key]: null } }, { op: 'set', id, patch: { [key]: value } }] : [{ op: 'set', id, patch: { [key]: value } }];
}

export function replaceDefinition(op: 'measure' | 'param' | 'query', id: string, key: string, value: unknown): Array<BoardOperation> {
  return grouped(value) ? [{ op, id, def: { [key]: null } }, { op, id, def: { [key]: value } }] : [{ op, id, def: { [key]: value } }];
}

const MONO = { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 12 };

const asText = (value: unknown) => (typeof value === 'string' ? value : JSON.stringify(value, null, 2));

function parsed(text: string): { value?: unknown; error?: string } {
  const trimmed = text.trim();
  if (trimmed === '') return { value: null };
  try {
    return { value: JSON.parse(trimmed) };
  } catch {
    return /^[[{"]/.test(trimmed) ? { error: 'This isn’t valid JSON yet.' } : { value: trimmed };
  }
}

function RawField({ name, value, disabled, onSet }: { name: string; value: unknown; disabled: boolean; onSet: (value: unknown) => void }) {
  const [draft, setDraft] = useState(asText(value));
  const [error, setError] = useState<string>();
  useEffect(() => {
    setDraft(asText(value));
    setError(undefined);
  }, [value]);
  const remove = (
    <IconButton aria-label={`Remove ${name}`} disabled={disabled} onClick={() => onSet(null)} size="small">
      <CloseIcon fontSize="small" />
    </IconButton>
  );
  if (typeof value === 'boolean')
    return (
      <Stack alignItems="center" direction="row" justifyContent="space-between">
        <FormControlLabel control={<Switch checked={value} disabled={disabled} onChange={(event) => onSet(event.target.checked)} size="small" />} label={<Typography sx={MONO}>{name}</Typography>} />
        {remove}
      </Stack>
    );
  const commit = () => {
    if (draft === asText(value)) return;
    if (typeof value === 'number') {
      const number = Number(draft);
      if (draft.trim() === '' || !Number.isFinite(number)) return setError('A number, please.');
      return onSet(number);
    }
    if (typeof value === 'string') return onSet(draft === '' ? null : draft);
    const next = parsed(draft);
    if (next.error) return setError(next.error);
    return onSet(next.value);
  };
  return (
    <Stack alignItems="flex-start" direction="row" spacing={0.5}>
      <TextField
        disabled={disabled}
        error={Boolean(error)}
        fullWidth
        helperText={error}
        inputProps={{ spellCheck: false, style: typeof value === 'object' ? MONO : undefined }}
        label={name}
        maxRows={14}
        multiline={typeof value === 'object' || (typeof value === 'string' && value.length > 40)}
        onBlur={commit}
        onChange={(event) => {
          setDraft(event.target.value);
          setError(undefined);
        }}
        size="small"
        value={draft}
      />
      {remove}
    </Stack>
  );
}

export default function RawSettings({ title, values, skip = [], disabled, onSet, inline = false }: RawSettingsProps) {
  const [toggled, setOpen] = useState(false);
  const open = inline || toggled;
  const [key, setKey] = useState('');
  const [text, setText] = useState('');
  const entries = Object.entries(values).filter(([name, value]) => !skip.includes(name) && value !== undefined);
  const added = parsed(text);
  return (
    <Stack data-lens-raw-settings={title} spacing={1}>
      {inline ? null : (
        <Box>
          <Button aria-expanded={open} onClick={() => setOpen(!open)} size="small" sx={{ textTransform: 'none' }} variant="text">
            {open ? `Hide ${title.toLowerCase()}` : `${title} (${entries.length})`}
          </Button>
        </Box>
      )}
      {open ? (
        <Stack spacing={1.25} sx={inline ? undefined : { borderLeft: 2, borderColor: 'divider', pl: 1.5 }}>
          {inline ? null : (
            <Typography color="text.secondary" variant="caption">
              Every setting as stored, including ones with no control of their own. Lists and groups are JSON; clearing a field removes it.
            </Typography>
          )}
          {entries.map(([name, value]) => (
            <RawField disabled={disabled} key={name} name={name} onSet={(next) => onSet(name, next)} value={value} />
          ))}
          <Stack direction="row" spacing={1}>
            <TextField inputProps={{ spellCheck: false, style: MONO }} label="New setting" onChange={(event) => setKey(event.target.value)} size="small" sx={{ flex: 1 }} value={key} />
            <TextField error={Boolean(added.error)} inputProps={{ spellCheck: false, style: MONO }} label="Value" onChange={(event) => setText(event.target.value)} size="small" sx={{ flex: 2 }} value={text} />
            <Button
              disabled={disabled || !key.trim() || Boolean(added.error) || added.value === null}
              onClick={() => {
                onSet(key.trim(), added.value);
                setKey('');
                setText('');
              }}
              size="small"
              variant="text"
            >
              Add
            </Button>
          </Stack>
        </Stack>
      ) : null}
    </Stack>
  );
}
